import { useMemo } from "react";
import { cx } from "~utils";

import InjectorAd from "~/features/ads/InjectorAd";
import NativeAd from "~features/ads/NativeAd";
import ArticleDisplay from "./ArticleDisplay";

import styles from "./article-list.module.css";
import SubPromo from "~features/homepage/SubPromo";

/**
 * @TODO will replace this with an actual ad component later.
 */
const AdContainer = ({ className, style, index }) => {
  return (
    // Pin ad to a specific row, articles will flow around it
    <div className={className} style={{ ...style, gridRow: index * 3 }}>
      <InjectorAd />
    </div>
  );
};

const NativeContainer = ({ className, isArchive }) => {
  return (
    <>
      <NativeAd className={className} position="native-grid" />
      {isArchive ? <SubPromo size="large" option="variantOne" /> : null}
    </>
  );
};

/**
 * General article list/grid.
 *
 * @param {{
 *  className: string
 *  results: Record<string,any>[] - Array of articles
 *  lazyLoadAfter: number - How many of the first articles should _not_ be lazy-loaded
 *  ads: Boolean - should injector ads be inserted
 *  native: Boolean - should native ads be inserted
 *  adFrequency: number - how many stories between ads
 *  ItemComponent: React.JSX - override the article component
 *  isSponsored: boolean - all content in this grid is sponsored
 *  maxExpectedItems: (null|boolean) - If the list is paginated how many items makes it full?
 * }} param0
 * @returns
 */
export function ArticleList({
  className,
  ItemComponent = ArticleDisplay,
  results = [],
  lazyLoadAfter = 0,
  ads = true,
  native = false,
  adFrequency = 6,
  clickTrackKey = "",
  isSponsored = false,
  isArchive = false,
  maxExpectedItems = 12,
  articleTitleTagType = "h2",
}) {
  const items = useMemo(
    () => injectAds(results, ads && adFrequency, native),
    [results, ads, native, adFrequency],
  );

  // Native will push items around in the list and replace the last item if the page is full.
  // If the page is not full we don't want to hide the last item when a native ad loads.
  const pageIsFull = maxExpectedItems !== null && items.length >= maxExpectedItems;

  return (
    <div className={cx(styles.articleList, className)}>
      <div
        className={cx(
          styles.articleListGrid,
          pageIsFull && styles.containerHideLastItemIfNativeLoads,
        )}
      >
        {items.map(({ type, article, index }, i) => {
          if (type === "article") {
            return (
              <ItemComponent
                className={styles.article}
                lazyload={i && i >= lazyLoadAfter}
                article={article}
                key={`article-${article.slug || index}`}
                clickTrackKey={clickTrackKey}
                clickTrackIndex={index}
                inAllSponsorContentList={isSponsored}
                articleTitleTagType={articleTitleTagType}
              />
            );
          }

          if (type === "nativeAd") {
            return (
              <NativeContainer
                className={styles.native}
                key={`native-${index}`}
                isArchive={isArchive}
              />
            );
          }

          if (type === "ad") {
            return <AdContainer className={styles.break} index={index} key={`ad-${index}`} />;
          }

          return null;
        })}
      </div>
    </div>
  );
}

/**
 * Injects ads into an article list.
 * @template Input - The article type
 * @param {Array<Input>} articles
 * @param {number} [frequency]
 * @returns {Array<{ type: "ad" | "nativeAd", index: number } | { type: "article", index: number, article: Input }>}
 */
const injectAds = (articles, frequency = 6, native = false) => {
  // Counters for ads
  let nativeIndex = 0;
  let adIndex = 0;

  return articles.flatMap((article, i) => {
    if (i && native && i === 2) {
      nativeIndex++;
      return [
        { type: "nativeAd", index: nativeIndex },
        { type: "article", index: i, article },
      ];
    }

    // Injects an ad every {frequency} articles
    if (i && frequency && i % frequency === 0) {
      adIndex++;
      return [
        { type: "ad", index: adIndex },
        { type: "article", index: i, article },
      ];
    }

    return { type: "article", index: i, article };
  });
};

export default ArticleList;
